import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Image,
  ImageSourcePropType,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import {
  BottomDrawer,
  Button,
  BUTTON_STATUS,
  COLORS,
  COMMON_STYLES,
  DefaultProductImage,
  LazyImage,
  SIZE,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import {
  DemoMaterialProduct,
  DemoMaterialProductVariant,
} from 'app/models/RequestFlow'

/** Demo variant sheet uses neutral10 surface (Figma Gray Light -10). */
const SHEET_BG = COLORS.neutral10

/** API does not expose a max units cap; align with typical issuance upper bound. */
const MAX_DEMO_ISSUANCE_UNITS = 99

type Props = {
  visible: boolean
  product: DemoMaterialProduct | null
  onClose: () => void
  onContinue: (
    product: DemoMaterialProduct,
    variant: DemoMaterialProductVariant,
    units: number,
  ) => void
}

const getActiveVariants = (product: DemoMaterialProduct | null) =>
  (product?.product_variants || []).filter(v => v.active)

const DemoProductVariantDrawer = ({
  visible,
  product,
  onClose,
  onContinue,
}: Props) => {
  const { t } = useTranslation()
  const { mediaStyles } = useContext(ResponsiveContext)
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null)
  const [units, setUnits] = useState(1)

  const activeVariants = useMemo(() => getActiveVariants(product), [product])

  useEffect(() => {
    if (!visible || !product) {
      setSelectedVariantId(null)
      setUnits(1)
      return
    }
    if (activeVariants.length === 1) {
      setSelectedVariantId(activeVariants[0].id)
      setUnits(1)
    } else {
      setSelectedVariantId(null)
      setUnits(1)
    }
  }, [visible, product, activeVariants])

  const selectedVariant = activeVariants.find(v => v.id === selectedVariantId)

  const selectVariant = (variantId: number) => {
    setSelectedVariantId(variantId)
    setUnits(1)
  }

  if (!product) return null

  return (
    <BottomDrawer
      visible={visible}
      heading={product.name}
      closeOnBackdropPress
      onClose={onClose}
      modalStyle={[
        styles.modalShell,
        mediaStyles({
          desktopStyle: styles.modalShellDesktop,
          mobileStyle: styles.modalShellMobile,
        }),
      ]}
    >
      <View style={styles.root}>
        {activeVariants.length === 0 ? (
          <Text style={styles.hint}>{t('demo_no_active_variants')}</Text>
        ) : (
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            nestedScrollEnabled
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {activeVariants.map(variant => {
              const isSelected = selectedVariantId === variant.id
              return (
                <View key={variant.id} style={styles.variantCard}>
                  <View style={styles.variantRow}>
                    <View style={styles.variantMeta}>
                      <LazyImage
                        imageUrl={product.image_url}
                        fallBackIcon={
                          <Image
                            source={DefaultProductImage as ImageSourcePropType}
                            style={styles.variantImage}
                          />
                        }
                        imgStyles={styles.variantImage}
                      />
                      <Text style={styles.variantName} numberOfLines={2}>
                        {variant.attributes_name.trim() || '-'}
                      </Text>
                    </View>
                    {isSelected ? (
                      <View style={styles.stepper}>
                        <Pressable
                          style={styles.stepperSide}
                          onPress={() => setUnits(prev => Math.max(1, prev - 1))}
                          accessibilityRole="button"
                          accessibilityLabel={t('demo_qty_decrease')}
                        >
                          <Text style={styles.stepperSideText}>−</Text>
                        </Pressable>
                        <Text style={styles.stepperValue}>{units}</Text>
                        <Pressable
                          style={styles.stepperSide}
                          onPress={() => {
                            if (units >= MAX_DEMO_ISSUANCE_UNITS) {
                              ToastAndroid.show(t('demo_qty_max_reached'), ToastAndroid.SHORT)
                              return
                            }
                            setUnits(prev => prev + 1)
                          }}
                          accessibilityRole="button"
                          accessibilityLabel={t('demo_qty_increase')}
                        >
                          <Text style={styles.stepperSideText}>+</Text>
                        </Pressable>
                      </View>
                    ) : (
                      <Pressable
                        style={styles.addBtn}
                        onPress={() => selectVariant(variant.id)}
                        accessibilityRole="button"
                        accessibilityLabel={t('demo_variant_add')}
                      >
                        <Text style={styles.addBtnText}>{t('demo_variant_add')}</Text>
                      </Pressable>
                    )}
                  </View>
                </View>
              )
            })}
          </ScrollView>
        )}

        <View style={styles.footerAction}>
          <Button
            text={t('demo_continue')}
            size={SIZE.large}
            status={
              selectedVariant ? BUTTON_STATUS.ACTIVE : BUTTON_STATUS.DISABLED
            }
            buttonStyle={styles.continueBtn}
            onClick={() => {
              if (!selectedVariant) {
                ToastAndroid.show(t('demo_select_product_variant'), ToastAndroid.SHORT)
                return
              }
              onContinue(product, selectedVariant, units)
            }}
          />
        </View>
      </View>
    </BottomDrawer>
  )
}

const styles = StyleSheet.create({
  modalShell: {
    backgroundColor: SHEET_BG,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  modalShellMobile: {
    width: '100%',
    maxWidth: '100%',
  },
  modalShellDesktop: {
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
  },
  /** Cancel BottomDrawer children wrapper `p1` so footer sits flush on sheet edge */
  root: {
    width: '100%',
    backgroundColor: SHEET_BG,
    flexDirection: 'column',
    marginHorizontal: -16,
    marginBottom: -16,
  },
  scroll: {
    flexGrow: 0,
    flexShrink: 1,
    backgroundColor: SHEET_BG,
  },
  scrollContent: {
    backgroundColor: SHEET_BG,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 8,
    gap: 12,
  },
  variantCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  variantRow: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.justifyBetween,
    minHeight: 40,
  },
  variantMeta: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    flex: 1,
    columnGap: 4,
    minWidth: 0,
    paddingRight: 8,
  },
  variantImage: {
    width: 40,
    height: 40,
    borderRadius: 7,
  },
  variantName: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
    flex: 1,
  },
  addBtn: {
    width: 80,
    height: 36,
    borderRadius: 8,
    backgroundColor: COLORS.primary100,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  addBtnText: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    color: COLORS.white,
  },
  stepper: {
    width: 80,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.primary100,
    backgroundColor: COLORS.primary10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    flexShrink: 0,
  },
  stepperSide: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepperSideText: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textPrimary100,
    lineHeight: 16,
  },
  stepperValue: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textPrimary100,
    minWidth: 20,
    textAlign: 'center',
  },
  footerAction: {
    backgroundColor: SHEET_BG,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
    width: '100%',
  },
  continueBtn: {
    width: '100%',
    maxWidth: 328,
    alignSelf: 'center',
    minHeight: 48,
  },
  hint: {
    ...TYPOGRAPHY.TEXT2,
    ...TEXT_STYLES.textNeutral70,
    paddingHorizontal: 16,
    paddingVertical: 24,
    backgroundColor: SHEET_BG,
  },
})

export default DemoProductVariantDrawer

export { getActiveVariants }
